/**
 * Locale dictionaries for the plugin-manager tab. The zh dictionary is the
 * key source; the en dictionary mirrors the exact key set.
 * @module @linxin666/dsh-client-ui-plugin-manager/client
 */

/** Simplified Chinese copy (the key-set source of truth). */
const STATIC_ZH = {
  tab: '插件管理',
  loading: '正在读取插件…',
  empty: '尚未安装任何用户插件。',
  userPlugins: '用户插件',
  products: '内置产品',
  conflictTitle: '安装冲突',
  conflictDisabled: '已自动禁用 {name}，避免重复挂载。',
  conflictEnabled: '已重新启用 {name}。',
  conflictChanged: '{name} 的状态已变更。',
  undoConflict: '撤销',
  conflictHint: '冲突动作在安装时自动执行，撤销会写回开关并在重启后生效。',
  installPlaceholder: 'npm 包名（如 @scope/name）或 git 仓库 URL',
  install: '安装',
  installing: '安装中…',
  installHint: '在 GitHub 或 npm 找到插件后，粘贴其 npm 包名或仓库地址安装；应用重启后插件生效。',
  version: '已安装 {version}',
  latest: '最新 {version}',
  update: '更新',
  updating: '更新中…',
  checkUpdates: '检查更新',
  checking: '检查中…',
  noUpdates: '所有插件都是最新版本。',
  updateRequiresDsh: '需要 DSH ≥ {min}',
  updateBlockedDsh: '需要 DSH ≥ {min}，请先升级 DSH 再更新',
  uninstall: '卸载',
  uninstallConfirmTitle: '卸载插件',
  uninstallConfirmBody: '将删除 {name} 的安装目录与配置行，并在重启后生效。',
  confirm: '确认卸载',
  cancel: '取消',
  restartHint: '插件变更将在重启应用后生效。',
  failed: '操作失败：{reason}',
  enabled: '已开启',
  disabled: '已关闭',
  mixed: '部分开启',
  unavailable: '不可用',
  uninstalled: '已卸载',
  source: '查看源码',
  applying: '正在应用更改…',
  fetching: '正在获取插件信息…',
  downloading: '正在下载…',
  downloadingPercent: '正在下载 {percent}%',
  extracting: '正在解压…',
  writing: '正在写入配置…',
  localOnlyTitle: '仅限本机操作',
  localOnlyBody: '为了保护主机配置，插件管理只能从本机打开。',
  failureBadge: '启动失败',
  failureGroupTitle: '其他启动失败',
  repair: '让 Agent 修复',
  repairing: '正在创建修复对话…',
  copyError: '复制错误',
  copied: '已复制',
  safeModeBanner: '安全模式：用户插件配置已跳过，插件开关不可用。',
  exitSafeMode: '恢复正常模式并重启',
  npmSource: 'npm',
  gitSource: 'git',
  enableSwitch: '开启 {name}',
  disableSwitch: '关闭 {name}',
  lockedRowHint: '核心行',
  childrenHint: '全家桶子插件可单独启停；停用后不再加载，代码仍随全家桶更新。',
  childrenSummary: '子插件 {enabled}/{total} 已启用',
  childrenShow: '展开 {name} 的子插件',
  childrenHide: '收起 {name} 的子插件',
  repairInstallTitle: '插件安装失败，请帮我诊断并修复。',
  repairInstallSpecLabel: '安装目标',
  repairInstallErrorLabel: '错误信息',
  repairInstallAsk: '请检查该插件并重新安装；完成后告诉我结果。',
  repairFailureTitle: '插件上次启动失败。请修复它。',
  repairFailurePluginLabel: '插件',
  repairFailureKindLabel: '失败类型',
  repairFailureAtLabel: '时间',
  repairFailureMessageLabel: '失败详情',
  repairFailureStackLabel: '原始堆栈',
  repairFailurePathLabel: '插件安装目录',
  repairFailureAsk: '请检查并修复该插件；修复完成后告诉我如何重新启用。',
  repairKindLoad: '加载失败',
  repairKindHang: '启动挂起',
  repairKindLate: '迟到拒绝',
  repairConflictTitle: '正在处理插件安装冲突',
  repairConflictPluginLabel: '冲突条目',
  repairConflictChangeLabel: '状态变化',
  repairConflictAsk: '请检查冲突双方的入口行 id 与挂载方式，消除重复挂载后告诉我如何重新启用。',
  repairStateEnabled: '已开启',
  repairStateDisabled: '已关闭',
  repairStateUninstalled: '已卸载',
} satisfies Record<string, string>

/** English copy, checked complete against the zh key set. */
const STATIC_EN = {
  tab: 'Plugin manager',
  loading: 'Reading plugins…',
  empty: 'No user plugins installed yet.',
  userPlugins: 'User plugins',
  products: 'Built-in products',
  conflictTitle: 'Install conflicts',
  conflictDisabled: 'Automatically disabled {name} to avoid double mounting.',
  conflictEnabled: 'Re-enabled {name}.',
  conflictChanged: 'The state of {name} changed.',
  undoConflict: 'Undo',
  conflictHint: 'Conflict actions run automatically at install time; undoing writes the switch back and applies after a restart.',
  installPlaceholder: 'npm package (e.g. @scope/name) or git repository URL',
  install: 'Install',
  installing: 'Installing…',
  installHint: 'Find a plugin on GitHub or npm, then paste its npm package name or repository URL; plugins load after a restart.',
  version: 'Installed {version}',
  latest: 'Latest {version}',
  update: 'Update',
  updating: 'Updating…',
  checkUpdates: 'Check for updates',
  checking: 'Checking…',
  noUpdates: 'All plugins are up to date.',
  updateRequiresDsh: 'Requires DSH >= {min}',
  updateBlockedDsh: 'Requires DSH >= {min}; upgrade DSH before updating',
  uninstall: 'Uninstall',
  uninstallConfirmTitle: 'Uninstall plugin',
  uninstallConfirmBody: '{name} and its configuration row will be removed; the change applies after a restart.',
  confirm: 'Uninstall',
  cancel: 'Cancel',
  restartHint: 'Plugin changes take effect after restarting the application.',
  failed: 'Operation failed: {reason}',
  enabled: 'On',
  disabled: 'Off',
  mixed: 'Partially on',
  unavailable: 'Unavailable',
  uninstalled: 'Uninstalled',
  source: 'View source',
  applying: 'Applying change…',
  fetching: 'Fetching plugin metadata…',
  downloading: 'Downloading…',
  downloadingPercent: 'Downloading {percent}%',
  extracting: 'Extracting…',
  writing: 'Writing configuration…',
  localOnlyTitle: 'Available on this computer only',
  localOnlyBody: 'To protect host configuration, plugin management is only available from a local browser.',
  failureBadge: 'Startup failure',
  failureGroupTitle: 'Other startup failures',
  repair: 'Ask the agent to fix',
  repairing: 'Opening repair conversation…',
  copyError: 'Copy error',
  copied: 'Copied',
  safeModeBanner: 'Safe mode: user plugin configuration is skipped; plugin switches are disabled.',
  exitSafeMode: 'Restore normal mode and restart',
  npmSource: 'npm',
  gitSource: 'git',
  enableSwitch: 'Turn on {name}',
  disableSwitch: 'Turn off {name}',
  lockedRowHint: 'Core row',
  childrenHint: 'Bundle child plugins toggle individually; a disabled child is never loaded, while its code still updates with the bundle.',
  childrenSummary: '{enabled}/{total} child plugins on',
  childrenShow: 'Show child plugins of {name}',
  childrenHide: 'Hide child plugins of {name}',
  repairInstallTitle: 'The plugin installation failed. Please diagnose and fix it.',
  repairInstallSpecLabel: 'Install target',
  repairInstallErrorLabel: 'Error message',
  repairInstallAsk: 'Please inspect the plugin and reinstall it; tell me when it is done.',
  repairFailureTitle: 'A plugin failed to start last time. Please fix it.',
  repairFailurePluginLabel: 'Plugin',
  repairFailureKindLabel: 'Failure kind',
  repairFailureAtLabel: 'At',
  repairFailureMessageLabel: 'Failure details',
  repairFailureStackLabel: 'Original stack',
  repairFailurePathLabel: 'Plugin install directory',
  repairFailureAsk: 'Please inspect and fix the plugin; tell me how to re-enable it afterwards.',
  repairKindLoad: 'Load failure',
  repairKindHang: 'Startup hang',
  repairKindLate: 'Late rejection',
  repairConflictTitle: 'Handling a plugin install conflict',
  repairConflictPluginLabel: 'Conflicting entry',
  repairConflictChangeLabel: 'State change',
  repairConflictAsk: 'Inspect the entry ids and mounting of both sides, resolve the double mount, and tell me how to re-enable.',
  repairStateEnabled: 'On',
  repairStateDisabled: 'Off',
  repairStateUninstalled: 'Uninstalled',
} satisfies Record<keyof typeof STATIC_ZH, string>

export const zh = STATIC_ZH
export const en = STATIC_EN
export type PluginManagerKey = keyof typeof STATIC_ZH
